import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { useMemberApi } from '../../hooks/useMemberApi';
import { useMemberFirearmApi } from '../../hooks/useMemberFirearmApi';
import { useMemberCompetencyApi } from '../../hooks/useMemberCompetencyApi';
import { useInvoiceApi } from '../../hooks/useInvoiceApi';
import { getMemberId, getRoles } from '../../utils/tokenUtils';
import { MemberDTO, MemberFirearmDTO, MemberCompetencyDTO, InvoiceDTO } from '../../services/apiClient';
import { MemberStatusConstants } from '../../constants/MemberStatusConstants';
import { InvoiceStatusConstants } from '../../constants/InvoiceStatusConstants';
import ComplianceOverview from '../../components/dashboards/ComplianceOverview';
import ComplianceHistoryOverview from '../../components/dashboards/ComplianceHistoryOverview';
import ComplianceHistoryFull from '../../components/drawer/ComplianceHistoryFull';
import FirearmOverview from '../../components/dashboards/FirearmOverview';
import CompetencyOverview from '../../components/dashboards/CompetencyOverview';
import MembershipStatusOverview from '../../components/dashboards/MembershipStatusOverview';
import Drawer from '../../components/common/Drawer';
import QuickLinks from '../../components/dashboards/QuickLinks';
import FriendsAndPartners from '../../components/dashboards/FriendsAndPartners';
import CompetitionBanners from '../../components/dashboards/CompetitionBanners';
import SimpleAlert from '../../components/common/SimpleAlert';
import InvoiceCard from '../../components/common/InvoiceCard';
import { Loader2 } from 'lucide-react';
import ImportantResources from '../../components/dashboards/ImportantResources';
import { FileViewer } from '../../components/common/FileViewer';
import { getComplianceYearBoundaries, isDedicatedCategory } from '../../utils/complianceUtils';

const Dashboard: React.FC = () => {
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Dashboard', href: '#' },
  ];

  const roles = getRoles();
  const memberId = getMemberId();

  const { getById, getMemberCard } = useMemberApi();
  const { search } = useMemberFirearmApi();
  const { search: searchCompetencies } = useMemberCompetencyApi();
  const { searchMemberInvoices } = useInvoiceApi();
  const [member, setMember] = useState<MemberDTO | null>(null);
  const [memberFirearms, setMemberFirearms] = useState<MemberFirearmDTO[]>([]);
  const [memberCompetencies, setMemberCompetencies] = useState<MemberCompetencyDTO[]>([]);
  const [invoices, setInvoices] = useState<InvoiceDTO[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);

  // FileViewer state
  const [viewingFile, setViewingFile] = useState<{ name: string; url: string } | null>(null);
  const [loadingMemberCard, setLoadingMemberCard] = useState<boolean>(false);

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerView, setDrawerView] = useState<'fullHistory' | 'invoices'>();

  // Drawer handlers
  const handleFullHistoryClick = () => {
    setDrawerView('fullHistory');
    setIsDrawerOpen(true);
  };

  const handleInvoicesClick = () => {
    setDrawerView('invoices');
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setDrawerView(undefined);
  };

  // Member card handler
  const handleMemberCardClick = async () => {
    if (loadingMemberCard) return;
    setLoadingMemberCard(true);
    try {
      const cardUrl = await getMemberCard(memberId!);
      setViewingFile({
        name: 'Member-Card.pdf',
        url: cardUrl,
      });
    } catch (error) {
      console.error('Error generating member card:', error);
    } finally {
      setLoadingMemberCard(false);
    }
  };

  // Get compliance message for the member
  const getComplianceMessage = () => {
    if (!member?.dateOfBirth || !member?.memberCategoryId || !isDedicatedCategory(member.memberCategoryId)) {
      return null;
    }

    const boundaries = getComplianceYearBoundaries(member.dateOfBirth);
    if (!boundaries) return null;

    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const nextComplianceDate = formatDate(boundaries.end);
    const startDate = formatDate(boundaries.start);
    const endDate = formatDate(boundaries.end);
    const complianceYear = boundaries.end.getFullYear();

    return (
      <>
        Your next compliance date is <strong>{nextComplianceDate}</strong>, all your activities logged between <strong>{startDate}</strong> and <strong>{endDate}</strong> will count towards your dedicated status compliance for <strong>{complianceYear}</strong>.
      </>
    );
  };

  // Helper functions for member status checking
  const isNonActiveMember = () => {
    if (!member?.memberStatusId) return false;
    const statusId = member.memberStatusId.toLowerCase();
    return (
      statusId !== MemberStatusConstants.ACTIVE.toLowerCase() &&
      statusId !== MemberStatusConstants.NEW_REGISTRATION.toLowerCase()
    );
  };

  const hasOutstandingInvoices = () => {
    return invoices.some(
      (invoice) =>
        invoice.invoiceStatusId?.toLowerCase() !== InvoiceStatusConstants.INVOICED.toLowerCase()
    ) && member?.memberStatusId?.toLowerCase() == MemberStatusConstants.ARREARS.toLowerCase();
  };

  useEffect(() => {
    const fetchMember = async () => {
      try {
        const response = await getById(memberId!);
        setMember(response?.data || null);
      } catch (error) {
        console.error('Error fetching Member', error);
      }
    };

    const fetchMemberFirearms = async () => {
      try {
        const response = await search({
          filters: { memberId },
          page: 1,
          pageSize: 9999,
          sortBy: 'CreatedUtc',
          sortDescending: true,
        });
        setMemberFirearms(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching Member Firearms', error);
      }
    };

    const fetchMemberCompetencies = async () => {
      try {
        const response = await searchCompetencies({
          filters: { memberId },
          page: 1,
          pageSize: 9999,
          sortBy: 'CreatedUtc',
          sortDescending: true,
        });
        setMemberCompetencies(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching Member Competencies', error);
      }
    };

    if (memberId) {
      fetchMember();
      fetchMemberFirearms();
      fetchMemberCompetencies();
    }
  }, [memberId]);

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!memberId) return;
      setIsLoadingInvoices(true);
      try {
        const response = await searchMemberInvoices(memberId);
        setInvoices(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching invoices:', error);
      } finally {
        setIsLoadingInvoices(false);
      }
    };
    fetchInvoices();
  }, [memberId]);

  // Restricted view for non-active members
  const renderRestrictedView = () => (
    <div className="p-5">
      {/* Welcome Section */}
      {member && (
        <div className="mb-6 hidden md:block">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Hello {member.firstName}!
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Welcome to your member portal
          </p>
          <p className="text-gray-600 dark:text-gray-400">
            Your <span className="font-semibold">FCA</span> status is:{' '}
            <span className="font-semibold">{member.memberCategory?.title || 'N/A'}</span>
          </p>
        </div>
      )}

      {/* Outstanding Account Warning */}
      {hasOutstandingInvoices() ? (
        <>
          <SimpleAlert
            variant="warning"
            message="Account is outstanding"
          />

          {/* Invoices Section */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              History
            </h2>
            {isLoadingInvoices ? (
              <div className="flex justify-center py-8">
                <Loader2 className="animate-spin h-8 w-8 text-gray-400" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No invoices found for this member.
              </div>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <InvoiceCard key={invoice.id} invoice={invoice} />
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <SimpleAlert
          variant="warning"
          message="Account is not available"
        />
      )}

    </div>
  );

  return (
    <AdminLayout
      activeMenuItem="/admin/dashboard"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin', 'User']}
      requiredPermission="Main.Dashboard.View"
    >

      {roles?.includes('User') && (
        <>
          {isNonActiveMember() ? (
            renderRestrictedView()
          ) : (
            <div className='p-5'>
              {/* Welcome Section */}
              {member && (
                <div className="mb-6 hidden md:block">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    Hello {member.firstName}!
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400">
                    Welcome to your member portal
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    Your <span className="font-semibold">FCA</span>  status is: <span className="font-semibold">{member.memberCategory?.title || 'N/A'}</span>
                  </p>
                </div>
              )}

              {/* Compliance Date Alert */}
              {member && getComplianceMessage() && (
                <div className="hidden md:block">
                  <SimpleAlert
                    variant="warning"
                    message={getComplianceMessage()}
                  />
                </div>
              )}

              {/* Top row: 3 status cards (Membership, Firearm, Competency) */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {member && <MembershipStatusOverview member={member} />}
                <FirearmOverview items={memberFirearms} showExpiringSoon={false} gridLayout="single-row" />
                <CompetencyOverview items={memberCompetencies} showExpiringSoon={false} />
              </div>

              {/* Bottom row: Compliance dashboards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                {member && <ComplianceOverview member={member} />}
                {member && <ComplianceHistoryOverview member={member} onFullHistoryClick={handleFullHistoryClick} />}
              </div>
              <div>
                <CompetitionBanners />
              </div>
              <ImportantResources />
              <QuickLinks
                onMemberCardClick={handleMemberCardClick}
                loadingMemberCard={loadingMemberCard}
                onInvoicesClick={handleInvoicesClick}
                loadingInvoices={isLoadingInvoices}
              />
              <FriendsAndPartners />
            </div>
          )}
        </>
      )}

      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        title={drawerView === 'invoices' ? 'Invoices' : 'Full Compliance History'}
        size={'large'}
      >
        {drawerView === 'fullHistory' && member && (
          <ComplianceHistoryFull member={member} />
        )}
        {drawerView === 'invoices' && (
          <div>
            {isLoadingInvoices ? (
              <div className="flex justify-center py-8">
                <Loader2 className="animate-spin h-8 w-8 text-gray-400" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No invoices found for this member.
              </div>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <InvoiceCard key={invoice.id} invoice={invoice} />
                ))}
              </div>
            )}
          </div>
        )}
      </Drawer>

      {viewingFile && (
        <FileViewer
          fileName={viewingFile.name}
          sasUrl={viewingFile.url}
          onClose={() => setViewingFile(null)}
        />
      )}

    </AdminLayout>
  );
};

export default Dashboard;
